/**
 * Sign in, and the "forgot password" request, on one route.
 *
 * Follows Kanvo's login layout (split panel, rounded fields, show/hide
 * password, reset as a second view of the same page). Left out on purpose:
 * Google, Microsoft and SAML buttons, because no provider is configured for
 * Cubby and signInWithOAuth creates a user when none exists, which would be an
 * open registration path while sign-up is closed.
 */

import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { AuthShell } from "../components/AuthShell";
import { Turnstile } from "../components/Turnstile";
import { ErrorBox, Field, inputClass, primaryButtonClass } from "../components/form";
import { EyeIcon, EyeOffIcon } from "../components/icons";

type View = "signin" | "reset";

export function Login() {
  const { supabase, config, session } = useSession();
  const location = useLocation();

  const [view, setView] = useState<View>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const needsCaptcha = config.turnstileSiteKey !== null;
  const destination = (location.state as { from?: string } | null)?.from ?? "/";
  const waitingOnCaptcha = needsCaptcha && !captchaToken;

  if (session) return <Navigate to={destination} replace />;

  // Turnstile tokens are single use. Remounting the widget is the only
  // reliable way to get a new one; see the note in Turnstile.tsx.
  function freshChallenge() {
    setCaptchaToken(null);
    setWidgetKey((key) => key + 1);
  }

  function switchView(next: View) {
    setView(next);
    setError(null);
    setResetSent(false);
    freshChallenge();
  }

  async function handleSignIn(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (waitingOnCaptcha) {
      setError("Complete the security check below and try again.");
      return;
    }

    setBusy(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });
    setBusy(false);

    if (signInError) {
      // Supabase returns the same message for a wrong password and an unknown
      // address on purpose. Do not try to distinguish them here.
      setError(signInError.message);
      freshChallenge();
    }
    // On success the session listener in SessionProvider picks it up and the
    // Navigate above sends the person on.
  }

  async function handleReset(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Enter the email address you sign in with.");
      return;
    }
    if (waitingOnCaptcha) {
      setError("Complete the security check below and try again.");
      return;
    }

    setBusy(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    setBusy(false);
    freshChallenge();

    if (resetError) {
      setError(resetError.message);
    } else {
      setResetSent(true);
    }
  }

  const envTag =
    config.environment !== "production" ? (
      <p className="mt-8 text-center text-xs text-stone-400">{config.environment} environment</p>
    ) : null;

  if (view === "reset") {
    return (
      <AuthShell>
        <h1 className="font-display text-3xl font-semibold text-ink">Reset your password</h1>
        <p className="mt-2 text-stone-600">
          Enter your email and we'll send you a link to set a new password.
        </p>

        {error ? <ErrorBox message={error} /> : null}

        {resetSent ? (
          <p className="mt-8 rounded-xl bg-leaf-50 px-4 py-3 text-sm text-leaf-800">
            If {email.trim()} has a Cubby account, a reset link is on its way. Open it in this
            browser, since the link only works where it was requested.
          </p>
        ) : (
          <form onSubmit={handleReset} className="mt-8 space-y-5">
            <Field label="Email address" htmlFor="reset-email">
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
              />
            </Field>

            <button type="submit" disabled={busy || waitingOnCaptcha} className={primaryButtonClass}>
              {busy ? "Sending" : "Send reset link"}
            </button>

            <Turnstile
              key={widgetKey}
              siteKey={config.turnstileSiteKey}
              onToken={setCaptchaToken}
            />
          </form>
        )}

        <button
          type="button"
          onClick={() => switchView("signin")}
          className="mt-6 w-full text-center text-sm font-medium text-leaf-700 hover:text-leaf-800"
        >
          Back to sign in
        </button>

        {envTag}
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-semibold text-ink">Welcome back</h1>
      <p className="mt-2 text-stone-600">
        New to Cubby?{" "}
        <Link to="/signup" className="font-medium text-leaf-700 hover:text-leaf-800">
          Join the waitlist
        </Link>
      </p>

      {error ? <ErrorBox message={error} /> : null}

      <form onSubmit={handleSignIn} className="mt-8 space-y-5">
        <Field label="Email address" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Password" htmlFor="password">
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${inputClass} pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-700"
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </Field>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => switchView("reset")}
            className="text-sm font-medium text-leaf-700 hover:text-leaf-800"
          >
            Forgot password?
          </button>
        </div>

        <button type="submit" disabled={busy || waitingOnCaptcha} className={primaryButtonClass}>
          {busy ? "Signing in" : "Sign in"}
        </button>

        <Turnstile key={widgetKey} siteKey={config.turnstileSiteKey} onToken={setCaptchaToken} />
      </form>

      {envTag}
    </AuthShell>
  );
}
